import { useCallback, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

/**
 * Explicit policy consent before money changes hands.
 *
 * The customer must tick this THEMSELVES — it is never pre-checked and the action stays
 * blocked until they do. A pre-ticked box records nothing: the whole point is a deliberate
 * act by the customer, so `checked` always starts false and every dialog open resets it.
 *
 * Links are HOST-RELATIVE on purpose. They used to be hardcoded to itcitysoft.com, which
 * sent a customer checking out on ogrogoti.com to the ITCity domain mid-payment — a
 * white-label leak (Decision #21) and a trust break at the worst possible moment. Relative
 * hrefs keep them on the brand they are buying from; each host serves its own legal pages.
 * They open in a new tab so reading a policy never destroys an in-progress checkout.
 */
export function PolicyLinks() {
  const link = 'text-brand dark:text-brand-dm hover:underline font-medium';
  return (
    <>
      <a href="/terms" target="_blank" rel="noreferrer" className={link}>Terms &amp; Conditions</a>
      {', '}
      <a href="/privacy" target="_blank" rel="noreferrer" className={link}>Privacy Policy</a>
      {' and '}
      <a href="/refund" target="_blank" rel="noreferrer" className={link}>Return &amp; Refund Policy</a>
    </>
  );
}

/** The checkbox itself — reuse wherever a payment form already has a summary to sit under. */
export function PolicyConsentCheckbox({
  checked,
  onChange,
  error = false,
  id = 'policy-consent',
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  /** Show the "please tick this" state after a blocked submit attempt. */
  error?: boolean;
  id?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
        />
        <span>
          I have read and agree to the <PolicyLinks />.
        </span>
      </label>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-2 ml-6.5 text-sm text-rose-600 dark:text-rose-400">
          Please agree to the policies to continue.
        </p>
      )}
    </div>
  );
}

/**
 * Gate a one-click payment action behind the consent tick.
 *
 * Most payment actions here are a single button that hands straight off to the gateway
 * ("Pay now" on an invoice row, "Renew", "Upgrade") with no form to host an inline
 * checkbox. This wraps them in a small confirm dialog instead, so every payment surface
 * asks for the same consent in the same words.
 *
 *   const consent = usePaymentConsent();
 *   <Button onClick={() => consent.request(() => void payNow())}>Pay now</Button>
 *   {consent.dialog}
 */
export function usePaymentConsent() {
  const [pending, setPending] = useState<(() => void | Promise<void>) | null>(null);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(false);

  const request = useCallback((run: () => void | Promise<void>) => {
    // Always reset: consent is per-payment, never carried over from a previous one.
    setChecked(false);
    setError(false);
    setPending(() => run); // functional form — otherwise setState treats `run` as an updater
  }, []);

  const close = useCallback(() => {
    setPending(null);
    setChecked(false);
    setError(false);
  }, []);

  const confirm = useCallback(async () => {
    if (!checked) {
      setError(true);
      return;
    }
    const run = pending;
    close();
    await run?.();
  }, [checked, pending, close]);

  const dialog: ReactNode = (
    <Modal
      open={pending !== null}
      onClose={close}
      title="Before you continue"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button onClick={() => void confirm()} disabled={!checked}>Continue to payment</Button>
        </div>
      }
    >
      <PolicyConsentCheckbox checked={checked} onChange={(v) => { setChecked(v); if (v) setError(false); }} error={error} id="pay-consent" />
    </Modal>
  );

  return { request, dialog };
}
